import { buildInkFilters } from '@/infrastructure/engine2d/scene/buildInkFilters';
import { ImageLoaderManager } from '@/infrastructure/engine2d/assets/ImageLoaderManager';
import type { TImagePropertyPatch } from '@/domain/image/TImagePropertyPatch';
import type { Canvas, FabricImage } from 'fabric';

type TApplyImagePropertyParams = {
    fabricCanvas: Canvas;
    fImage: FabricImage;
    patch: TImagePropertyPatch;
};

// Awaited by the caller so the change it announces is already on the canvas.
// Fire-and-forget here meant the thumbnail was exported from the previous
// render, and the selection handles kept the old geometry until some later
// interaction happened to recompute them.
async function applyImagePropertyToFabric(params: TApplyImagePropertyParams): Promise<void> {
    const { fabricCanvas, fImage, patch } = params;

    if (patch.opacity !== undefined) {
        fImage.set('opacity', patch.opacity);
        fabricCanvas.renderAll();
    }
    // How the zone is printed, not what this image is: applied to the fabric object and left
    // out of the entity, so nothing of it reaches the saved project. `applyFilters` rebuilds
    // the object's own bitmap, which is what the 3D reads its texture from — so the cup
    // follows without anyone telling it to.
    if (patch.inkTreatment !== undefined) {
        fImage.filters = patch.inkTreatment === null ? [] : buildInkFilters(patch.inkTreatment);
        fImage.applyFilters();
        fabricCanvas.renderAll();
    }
    if (patch.sourceUrl === undefined) {
        return;
    }
    const img = await ImageLoaderManager.loadImage(patch.sourceUrl);
    if (img instanceof Error) {
        console.error('applyImagePropertyToFabric: failed to load the new image source', img);
        return;
    }
    fImage.setElement(img as HTMLImageElement);
    // setElement resizes the object but leaves aCoords / oCoords alone, so the
    // controls stay where the old image was until something forces a recompute.
    fImage.setCoords();
    fabricCanvas.renderAll();
}

export { applyImagePropertyToFabric };
